// src/components/Yritysprofiili/CompanyProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { MdArrowBack } from "react-icons/md";
 import { FaFacebook, FaInstagram, FaTiktok, FaYoutube, FaStar, FaRegStar } from "react-icons/fa";
import ResponsiveButton from "../ResponsiveButton/NeonButton.jsx";
import GigCalendar from "../Keikkakalenteri/Keikkakalenteri";
import BookingForm from "../Bookkauslomake/Bookkauslomake";
import BandGallery from "../Kuvagalleria/Kuvagalleria";
import { api, FALLBACK_BAND_IMAGE } from "../../services/api";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs.jsx";

const SERVICE_NAME = import.meta.env?.VITE_SERVICE_NAME ?? "Buukkeri";

/* ───── pienehköt apurit ───── */
const safeArr = (x) => (Array.isArray(x) ? x : []);
const normalizeUrl = (u) => (!u ? null : /^https?:\/\//i.test(String(u).trim()) ? String(u).trim() : `https://${String(u).trim()}`);
const toCommaString = (val, pick = ["name", "region"]) => {
  if (!val) return "";
  if (typeof val === "string") return val.trim();
  if (Array.isArray(val))
    return val
      .map((x) => (typeof x === "object" && x ? pick.map((k) => x?.[k]).find(Boolean) || "" : String(x || "").trim()))
      .filter(Boolean)
      .join(", ");
  if (typeof val === "object") return pick.map((k) => val?.[k]).find(Boolean) || "";
  return String(val);
};
const cleanMeta = (t, n = 160) => {
  const s = String(t || "").replace(/\s+/g, " ").trim();
  if (s.length <= n) return s;
  const cut = s.slice(0, n + 1);
  const sp = cut.lastIndexOf(" ");
  return (sp > 120 ? cut.slice(0, sp) : s.slice(0, n)).trim();
};

// Muunna YouTube-URL embed-muotoon
const toEmbed = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  const mShort = s.match(/^https?:\/\/youtu\.be\/([^?&#/]+)/i);
  if (mShort) return `https://www.youtube.com/embed/${mShort[1]}`;
  const mWatch = s.match(/[?&]v=([^?&#/]+)/i);
  if (mWatch) return `https://www.youtube.com/embed/${mWatch[1]}`;
  return s;
};

const toAbs = (u, origin) => {
  if (!u) return null;
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return `${origin}${s.startsWith("/") ? s : `/${s}`}`;
};

/* ───── itse komponentti ───── */
export default function CompanyProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const todayStr = () => new Date().toISOString().split("T")[0];
  const isYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get("date");
  const fromStorage = typeof window !== "undefined" ? localStorage.getItem("bilebox:selectedDate") : null;
  let initialDate = isYmd(fromUrl) ? fromUrl : isYmd(fromStorage) ? fromStorage : todayStr();
  if (initialDate < todayStr()) initialDate = todayStr();

  /* Haku */
  useEffect(() => {
    const ac = new AbortController();
    setError("");
    setCompany(null);
    (async () => {
      try {
        const data = await api.company(slug, { signal: ac.signal });
        setCompany(data?.company || data);
      } catch (e) {
        if (e.name !== "AbortError") setError("Jotain meni pieleen yrityksen tietojen hakemisessa.");
      }
    })();
    return () => ac.abort();
  }, [slug]);

  /* Countdown pendingille */
  useEffect(() => {
    if (selectedDate?.status !== "pending") return;
    const deadline = selectedDate?.expires_at
      ? new Date(selectedDate.expires_at).getTime()
      : selectedDate?.sent_at
      ? new Date(selectedDate.sent_at).getTime() + 24 * 60 * 60 * 1000
      : null;
    if (!deadline) return;
    const t = setInterval(() => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft("00:00:00");
        setSelectedDate(null);
        setRefreshKey(Date.now());
        clearInterval(t);
      } else {
        const h = Math.floor(diff / 36e5);
        const m = Math.floor((diff % 36e5) / 6e4);
        const s = Math.floor((diff % 6e4) / 1e3);
        setTimeLeft(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [selectedDate]);

    useEffect(() => {
    if (!company?.id) return;

    const ctrl = new AbortController();

    const send = () => {
        const payload = {
        target_type: "company",
        target_id: Number(company.id),
        slug: company.slug ?? null,
        };

        // yritä ensin sendBeaconia (varmin sivulta poistuttaessa)
        if (navigator?.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon("/api/analytics/view", blob);
        return;
        }

        // fallback: fetch keepalive
        fetch("/api/analytics/view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: ctrl.signal,
        keepalive: true,
        }).catch(() => {});
    };

    const t = setTimeout(send, 800); // pieni viive
    return () => { clearTimeout(t); ctrl.abort(); };
    }, [company?.id, company?.slug]);

  const handleDateSelect = (date, status = "free", sentAt = null, expiresAt = null) => {
    const d = new Date(date);
    const weekday = d.toLocaleDateString("fi-FI", { weekday: "long" }).replace(/na$/, "");
    setSelectedDate({
      iso: d.toISOString().split("T")[0],
      formatted: `${weekday} ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`,
      status,
      sent_at: sentAt,
      expires_at: expiresAt,
    });
  };

  /* ── HOOKIT (aina ennen early returneja) ── */
  const regionsText = useMemo(
    () => toCommaString(company?.performance_regions || company?.regions, ["name", "region"]),
    [company]
  );
  const videoUrls = useMemo(() => {
    const lines = (typeof company?.video_link === "string" ? company.video_link : "")
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);

    const fromArray = Array.isArray(company?.videos)
      ? company.videos.map((v) => v?.url).filter(Boolean)
      : [];

    const maybeFirst = company?.first_video_url ? [company.first_video_url] : [];

    const all = [...lines, ...fromArray, ...maybeFirst]
      .map(toEmbed)
      .filter(Boolean);

    return Array.from(new Set(all));
  }, [company]);

  // Arvostelut: turvallinen muunnos ja rajaus
    const ratingNumRaw = Number(company?.average_rating ?? company?.rating?.average);
    const ratingAvg = Number.isFinite(ratingNumRaw) ? Math.min(5, Math.max(0, ratingNumRaw)) : 0;

    const countRaw = Number(company?.total_reviews ?? company?.reviews_count ?? company?.rating?.total);
    const ratingCount = Number.isFinite(countRaw) ? Math.max(0, Math.floor(countRaw)) : 0;

  /* ── early returnit ── */
  if (error) {
    return (
      <div className="p-5">
        <p className="text-red-500">Virhe: {error}</p>
        <ResponsiveButton onClick={() => navigate(-1)}>Takaisin</ResponsiveButton>
      </div>
    );
  }
  if (!company) return <div className="p-5">Ladataan yrityksen tietoja…</div>;

  /* SEO */
  const origin = typeof window !== "undefined" ? window.location.origin : "https://spiderlegs.example";
  const canonicalUrl = `${origin}${location.pathname}`;
  const name = (company.name || "").trim();
  const title = `${SERVICE_NAME} | ${name} – yritysprofiili`;
  const metaDescription = cleanMeta(company.description || `${company.name} – yritysprofiili.`);
  const ogImage = toAbs(company.profile_image || FALLBACK_BAND_IMAGE, origin);

  /* Render-tiedot */
  const coverSrc = String(company?.profile_image || "").trim() || FALLBACK_BAND_IMAGE;
  const socials = {
    facebook: normalizeUrl(company.facebook) || normalizeUrl(company.socials?.facebook),
    instagram: normalizeUrl(company.instagram) || normalizeUrl(company.socials?.instagram),
    youtube: normalizeUrl(company.youtube) || normalizeUrl(company.socials?.youtube),
    tiktok: normalizeUrl(company.tiktok) || normalizeUrl(company.socials?.tiktok),
  };
  const galleryItems = safeArr(company.gallery_images).map((it, i) =>
    typeof it === "string"
      ? { src: it.trim(), alt: `${company.name} kuva #${i + 1}`, title: `${company.name} – kuva #${i + 1}` }
      : { src: it.src || it.url || "", alt: it.alt || `${company.name} kuva #${i + 1}`, title: it.title || `${company.name} – kuva #${i + 1}` }
  );

  return (
    <div className="pt-12 dark:bg-black dark:bg-opacity-20 pb-10 mt-2">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="h-10" />

        <div className=" ml-4 mb-4">
      
            {company && <Breadcrumbs currentLabel={company.name} />}
      
        </div>

      <ResponsiveButton
        onClick={() => {
          if (window.history.length > 2) navigate(-1);
          else navigate("/");
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
        }}
        variant="backbutton"
        className="ml-4"
      >
        <div className="flex items-center gap-2">
          <MdArrowBack className="text-xl" />
          <span>Takaisin</span>
        </div>
      </ResponsiveButton>

      {/* Nimi + kansikuva */}
      <div className="max-w-6xl mx-auto mt-6">
        <h1 className="font-customFont2 dark:text-shadow-lg text-center text-black dark:text-primary text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4">
          {company.name}
        </h1>

        <div className="max-w-[1104px] mx-auto overflow-visible rounded-lg shadow-md">
          <img
            src={coverSrc}
            alt={company.name}
            title={`${company.name} – kansikuva`}
            loading="eager"
            decoding="async"
            className="w-full h-auto object-cover"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = FALLBACK_BAND_IMAGE;
            }}
          />
        </div>
      </div>

      {/* Kuvaus */}
      {company.description && (
        <div className="max-w-6xl mx-auto mt-4 p-12 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-md">
          <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg">
            {company.description.split("\n").map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>
        </div>
      )}

      {/* Kuvagalleria */}
      {galleryItems.length > 0 && (
        <div className="max-w-6xl mx-auto p-4 mt-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-md">
          <BandGallery images={galleryItems} bandName={company.name} />
        </div>
      )}

      {/* VIDEOT | TIEDOT */}
      <div className="mt-6 max-w-6xl mx-auto p-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-lg grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
        {/* Videot */}
        {videoUrls.length > 0 && (
          <div className="flex flex-col gap-8">
            <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl font-bold leading-tight">
              Videot
            </h3>
            {videoUrls.map((videoUrl, idx) => (
              <iframe
                key={idx}
                width="100%"
                height="315"
                src={videoUrl}
                title={`Video ${idx + 1}`}
                frameBorder="0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              />
            ))}
          </div>
        )}

        {/* Tiedot */}
        <div className="flex flex-col">
          <div className="ml-4">
            <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl font-bold leading-tight mb-4">
              Tiedot
            </h3>

            {regionsText && (
              <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg font-light xxxsm:text-center xxsm:text-center s24:text-center xsm:text-center sm:text-center">
                <span className="font-semibold">Toiminta-alueet:</span> {regionsText}
              </p>
            )}

            {company.location && (
              <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg font-light xxxsm:text-center xxsm:text-center s24:text-center xsm:text-center sm:text-center">
                <span className="font-semibold">Kotipaikka:</span> {company.location}
              </p>
            )}

            {ratingCount > 0 ? (
           <div
                className="flex items-center mt-2"
                aria-label={`Arvosana ${ratingAvg.toFixed(1)} / 5, ${ratingCount} arvostelua`}
                role="img"
                >

                <span className="font-customFont1 text-left text-black/80 dark:text-primary text-lg">
                {ratingAvg.toFixed(1)} / 5
                </span>
                <div className="ml-2 flex" aria-hidden="true">
                {(() => {
                    const full = Math.floor(ratingAvg);
                    const half = ratingAvg - full >= 0.5 ? 1 : 0;
                    const empty = 5 - full - half;
                    return (
                    <>
                        {[...Array(full)].map((_, i) => (
                        <FaStar key={`f${i}`} className="text-yellow-400" />
                        ))}
                        {half ? (
                        <span className="relative inline-block" style={{ width: "1em", height: "1em" }}>
                        <FaRegStar className="text-gray-400 absolute inset-0" />
                        <FaStar className="text-yellow-400 absolute inset-0" style={{ clipPath: "inset(0 50% 0 0)" }} />
                        </span>

                        ) : null}
                        {[...Array(empty)].map((_, i) => (
                        <FaRegStar key={`e${i}`} className="text-gray-400" />
                        ))}
                    </>
                    );
                })()}
                </div>
                <span className="font-customFont1 text-black/80 dark:text-primary text-lg font-bold ml-3">
                ({ratingCount})
                </span>
            </div>
            ) : (
            <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg font-bold mt-2 mb-6">
                Ei vielä arvosteluja
            </p>
            )}

            {/* Palvelut */}
            {Array.isArray(company.services) && company.services.length > 0 && (
              <div className="max-w-6xl mx-auto p-6 mt-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-md">
                <h3 className="font-customFont2 text-center text-3xl dark:text-primary mb-4">Palvelut</h3>
                <div className="flex flex-wrap gap-2 md:gap-3 justify-center md:justify-start mdd:justify-start lg:justify-start xl:justify-start 2xl:justify-start">
                  {company.services.map((s) => (
                    <span
                      key={s}
                      className="px-3 py-1 rounded-full border border-white/10 bg-black/10 dark:bg-black/40 text-sm text-black/80 dark:text-white/80"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(socials.facebook || socials.instagram || socials.youtube || socials.tiktok) && (
              <div className="max-w-6xl mx-auto flex space-x-6 justify-center mt-6">
                {socials.facebook && (
                  <a href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label={`${company.name} Facebook`} className="group relative transition-transform transform hover:scale-125 duration-300">
                    <FaFacebook size={35} className="text-blue-600 hover:text-blue-700" />
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all bg-blue-600 text-white text-xs px-2 py-1 rounded-md">
                      Facebook
                    </span>
                  </a>
                )}
                {socials.instagram && (
                  <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label={`${company.name} Instagram`} className="group relative transition-transform transform hover:scale-125 duration-300">
                    <FaInstagram size={35} style={{ color: "#E1306C" }} />
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all bg-pink-500 text-white text-xs px-2 py-1 rounded-md">
                      Instagram
                    </span>
                  </a>
                )}
                {socials.tiktok && (
                  <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label={`${company.name} TikTok`} className="group relative transition-transform transform hover:scale-125 duration-300">
                    <FaTiktok className="text-black dark:text-white" size={35} />
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all bg-gray-800 text-white text-xs px-2 py-1 rounded-md">
                      TikTok
                    </span>
                  </a>
                )}
                {socials.youtube && (
                  <a href={socials.youtube} target="_blank" rel="noopener noreferrer" aria-label={`${company.name} YouTube`} className="group relative transition-transform transform hover:scale-125 duration-300">
                    <FaYoutube className="text-red-600" size={35} />
                    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all bg-red-600 text-white text-xs px-2 py-1 rounded-md">
                      YouTube
                    </span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KEIKKAKALENTERI | YHTEYDENOTTOLOMAKE */}
      <div className="max-w-6xl mx-auto mt-6 grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
        <div className="p-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg">
          <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl font-bold leading-tight">
            Varauskalenteri
          </h3>
         <GigCalendar
            companyId={company.id}
            onDateSelect={handleDateSelect}
            refreshKey={refreshKey}
            initialDate={initialDate}
            />
        </div>

        <div className="p-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-lg">
          <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl font-bold leading-tight mb-4">
            Yhteydenottolomake
          </h3>

          {selectedDate?.status === "booked" ? (
            <div className="bg-black/70 text-white rounded-lg p-6 text-center">
              Valitettavasti {company.name} on jo varattu kyseiselle päivälle. 😢
            </div>
          ) : (
            <>
              {selectedDate?.formatted && (
                <p className="font-customFont2 dark:text-white/90 text-black/90 text-lg font-semibold text-center mt-2">
                  Valittu päivä: <strong>{selectedDate.formatted}</strong>
                </p>
              )}
              {selectedDate?.status === "pending" && (
                <div className="bg-black/70 text-white rounded-lg p-4 text-center my-4">
                  <p className="mb-2">Tarjouspyyntö on käsittelyssä.</p>
                  <p>Raukeaa: <strong>{timeLeft ?? "..."}</strong></p>
                </div>
              )}
              <BookingForm
                companySlug={company.slug}        // <-- korjattu
                targetType="company"              // <-- korjattu
                selectedDate={selectedDate ?? null}
                defaultMode={selectedDate?.status === "pending" ? "message" : "quote"}
                quoteDisabled={selectedDate?.status === "pending"}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
